import type {
  Aircraft,
  AvailabilityWindow,
  Booking,
  BookingStatus,
  CertState,
  Lesson,
  Profile,
  ProgramLesson,
  ProgressRecord,
  Role,
} from "../lib/types";
import * as schoolsRepo from "../lib/repo/schools";
import * as profilesRepo from "../lib/repo/profiles";
import * as aircraftRepo from "../lib/repo/aircraft";
import * as lessonsRepo from "../lib/repo/lessons";
import * as programsRepo from "../lib/repo/programs";
import * as certsRepo from "../lib/repo/certs";
import * as availabilityRepo from "../lib/repo/availability";
import * as progressRepo from "../lib/repo/progress";
import * as bookingsRepo from "../lib/repo/bookings";
import { SEED_SCHOOL, SEED_LOOKUPS, SEED_BOOKINGS } from "../lib/seed";

export type { Role } from "../lib/types";

export interface BoardLookups {
  aircraft: Aircraft[];
  lessons: Lesson[];
  profiles: Profile[];
  certs: Record<string, CertState>;
  availability: Record<string, AvailabilityWindow[]>;
  progress: Record<string, ProgressRecord>;
  programLessons: ProgramLesson[];
}

export interface BoardStore {
  schoolId: string | null;
  schoolSlug: string | null;
  bookings: Booking[];
  lookups: BoardLookups;
  loading: boolean;
  loaded: boolean;
  error: string | null;
  filterAircraft: string;
  filterInstructor: string;
  scheduleView: "timeline" | "list";
  selectedStudentId: string;
  role: Role;
  loadDemo(date?: string): Promise<void>;
  loadForSchool(schoolId: string, date?: string): Promise<void>;
  refreshBookings(date?: string): Promise<void>;
  setRole(r: Role): void;
  upsertBooking(b: Booking): Promise<void>;
  cancelBooking(id: string): Promise<void>;
  setStatus(id: string, status: BookingStatus, ready?: boolean): Promise<void>;
  currentUserId(): string;
  studentList(): Profile[];
}

const today = (): string => new Date().toISOString().slice(0, 10);

const emptyLookups = (): BoardLookups => ({
  aircraft: [],
  lessons: [],
  profiles: [],
  certs: {},
  availability: {},
  progress: {},
  programLessons: [],
});

async function fetchAll(schoolId: string, date: string): Promise<{
  lookups: BoardLookups;
  bookings: Booking[];
}> {
  const [aircraft, lessons, profiles, certs, availability, progress, programs, bookings] =
    await Promise.all([
      aircraftRepo.listBySchool(schoolId),
      lessonsRepo.listBySchool(schoolId),
      profilesRepo.listBySchool(schoolId),
      certsRepo.mapBySchool(schoolId),
      availabilityRepo.mapBySchool(schoolId),
      progressRepo.mapBySchool(schoolId),
      programsRepo.listBySchool(schoolId),
      bookingsRepo.listForDate(schoolId, date),
    ]);
  const programLessons = await programsRepo.lessonsForPrograms(programs.map((p) => p.id));
  return {
    lookups: { aircraft, lessons, profiles, certs, availability, progress, programLessons },
    bookings,
  };
}

export function createBoardStore(): BoardStore {
  return {
    schoolId: null,
    schoolSlug: null,
    bookings: [],
    lookups: emptyLookups(),
    loading: false,
    loaded: false,
    error: null,
    filterAircraft: "all",
    filterInstructor: "all",
    scheduleView: "timeline",
    selectedStudentId: "",
    role: "Scheduler",

    async loadDemo(date) {
      this.loading = true;
      this.error = null;
      try {
        const school = await schoolsRepo.getDemoSchool();
        this.schoolId = school.id;
        this.schoolSlug = school.slug;
        const result = await fetchAll(school.id, date ?? today());
        const empty = result.lookups.aircraft.length === 0;
        this.lookups = empty ? SEED_LOOKUPS : result.lookups;
        this.bookings = empty ? SEED_BOOKINGS : result.bookings;
        this.loaded = true;
        if (!this.selectedStudentId) {
          const s = this.lookups.profiles.find((p) => p.role === "Student");
          if (s) this.selectedStudentId = s.id;
        }
      } catch (e) {
        this.schoolId = SEED_SCHOOL.id;
        this.schoolSlug = SEED_SCHOOL.slug;
        this.lookups = SEED_LOOKUPS;
        this.bookings = SEED_BOOKINGS;
        this.loaded = true;
        if (!this.selectedStudentId) {
          const s = SEED_LOOKUPS.profiles.find((p) => p.role === "Student");
          if (s) this.selectedStudentId = s.id;
        }
        console.warn("[board.loadDemo] backend unavailable, using seed data:", (e as Error).message);
      } finally {
        this.loading = false;
      }
    },

    async loadForSchool(schoolId, date) {
      this.loading = true;
      this.error = null;
      try {
        this.schoolId = schoolId;
        const school = await schoolsRepo.getSchoolById(schoolId);
        this.schoolSlug = school?.slug ?? null;
        const result = await fetchAll(schoolId, date ?? today());
        this.lookups = result.lookups;
        this.bookings = result.bookings;
        this.loaded = true;
      } catch (e) {
        this.error = (e as Error).message ?? "Failed to load school";
        this.loaded = true;
        console.error("[board.loadForSchool]", e);
      } finally {
        this.loading = false;
      }
    },

    async refreshBookings(date) {
      if (!this.schoolId) return;
      this.bookings = await bookingsRepo.listForDate(this.schoolId, date ?? today());
    },

    setRole(r) {
      this.role = r;
      if (r === "Student") {
        const s = this.lookups.profiles.find((p) => p.role === "Student");
        if (s) this.selectedStudentId = s.id;
      }
    },

    async upsertBooking(b) {
      if (!this.schoolId) return;
      try {
        await bookingsRepo.upsert({
          id: b.id,
          schoolId: this.schoolId,
          lessonId: b.lessonId,
          studentId: b.studentId,
          instructorId: b.instructorId,
          aircraftId: b.aircraftId,
          bookingDate: today(),
          start: b.start,
          end: b.end,
          status: b.status,
          ready: b.ready,
          notes: b.notes,
        });
        await this.refreshBookings();
      } catch (e) {
        this.error = (e as Error).message ?? "Failed to save booking";
        console.error("[board.upsertBooking]", e);
      }
    },

    async cancelBooking(id) {
      try {
        await bookingsRepo.cancel(id);
        await this.refreshBookings();
      } catch (e) {
        this.error = (e as Error).message ?? "Failed to cancel booking";
        console.error("[board.cancelBooking]", e);
      }
    },

    async setStatus(id, status, ready) {
      try {
        await bookingsRepo.setStatus(id, status, ready);
        await this.refreshBookings();
      } catch (e) {
        this.error = (e as Error).message ?? "Failed to update booking";
        console.error("[board.setStatus]", e);
      }
    },

    currentUserId() {
      const want = (role: Role) => this.lookups.profiles.find((p) => p.role === role)?.id ?? "";
      switch (this.role) {
        case "Owner": return want("Owner");
        case "Scheduler": return want("Scheduler");
        case "Flight Instructor": return want("Flight Instructor");
        case "Ground Instructor": return want("Ground Instructor");
        case "Student": return this.selectedStudentId || want("Student");
      }
    },

    studentList() {
      return this.lookups.profiles.filter((p) => p.role === "Student");
    },
  };
}
